import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useTheme } from "../App";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Building2, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Clock,
  LogOut,
  Settings,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Sun,
  Moon,
  CreditCard,
  Eye,
  EyeOff,
  Save,
  TestTube,
  Zap,
  Package,
  X,
  Mail,
  MoreHorizontal,
  Ban,
  Trash2,
  Database
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { RotalieLogo } from "../components/RotalieLogo";
import DatabaseManager from "../components/DatabaseManager";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isPlatformAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Cancel subscription state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingCompany, setCancellingCompany] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  
  // Delete company state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  
  // All available feature flags that can be toggled
  const ALL_FEATURE_FLAGS = {
    dayView: { name: "Day View", description: "View and manage daily schedules" },
    weekView: { name: "Week View", description: "View and manage weekly schedules" },
    basicReports: { name: "Basic Reports", description: "Simple scheduling reports" },
    advancedReports: { name: "Advanced Reports", description: "Detailed analytics and insights" },
    wageCostCalculations: { name: "Wage Cost Calculations", description: "Real-time cost tracking" },
    breakManagement: { name: "Break Management", description: "Configure and track breaks" },
    overtimeRates: { name: "Overtime & Custom Rates", description: "Multiple pay rates per employee" },
    absenceTracking: { name: "Absence & Leave Tracking", description: "Holiday and sick leave management" },
    clockInOut: { name: "Clock In/Out", description: "Time tracking for employees" },
    payrollIntegrations: { name: "Payroll Integrations", description: "Export to Sage, ADP, and more" },
    multipleLocations: { name: "Multiple Locations", description: "Manage staff across multiple sites" },
    multipleDepartments: { name: "Multiple Departments", description: "Organize staff by department" },
    csvExport: { name: "CSV Export", description: "Export data to CSV" },
    pdfExport: { name: "PDF Export", description: "Export reports to PDF" }
  };
  
  // Stripe settings state
  const [stripeSettings, setStripeSettings] = useState({
    test_publishable_key: "",
    test_secret_key: "",
    live_publishable_key: "",
    live_secret_key: "",
    is_live_mode: false,
    test_secret_key_set: false,
    live_secret_key_set: false
  });
  const [showTestSecretKey, setShowTestSecretKey] = useState(false);
  const [showLiveSecretKey, setShowLiveSecretKey] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);

  // Email (Resend) settings state
  const [emailSettings, setEmailSettings] = useState({
    api_key: "",
    sender_email: "onboarding@resend.dev",
    sender_name: "Rotalie",
    api_key_set: false
  });
  const [showEmailApiKey, setShowEmailApiKey] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // Packages state
  const [packages, setPackages] = useState([]);
  const [currencies, setCurrencies] = useState({});
  const [editingPackage, setEditingPackage] = useState(null);
  const [savingPackage, setSavingPackage] = useState(false);

  // Redirect if not platform admin
  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate("/login");
    }
  }, [isPlatformAdmin, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsRes, companiesRes, settingsRes, stripeRes, packagesRes, emailRes] = await Promise.all([
          api.get("/platform/stats"),
          api.get("/platform/companies"),
          api.get("/platform/settings"),
          api.get("/platform/stripe-settings"),
          api.get("/admin/packages"),
          api.get("/admin/email-settings")
        ]);
        setStats(statsRes.data);
        setCompanies(companiesRes.data);
        setSettings(settingsRes.data);
        setStripeSettings({
          ...stripeRes.data,
          test_secret_key: "",
          live_secret_key: ""
        });
        setPackages(packagesRes.data.packages || []);
        setCurrencies(packagesRes.data.currencies || {});
        setEmailSettings({
          ...emailRes.data,
          api_key: ""
        });
      } catch (error) {
        console.error("Error fetching platform data:", error);
        toast.error("Failed to load some platform data");
      } finally {
        setLoading(false);
      }
    };

    if (isPlatformAdmin) {
      fetchData();
    }
  }, [isPlatformAdmin]);

  const handleSaveSettings = async () => {
    if (!settings) return;
    
    setSavingSettings(true);
    try {
      await api.put("/platform/settings", settings);
      toast.success("Platform settings updated");
      setSettingsDialogOpen(false);
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveStripeSettings = async () => {
    setSavingStripe(true);
    try {
      const updateData = {
        test_publishable_key: stripeSettings.test_publishable_key,
        live_publishable_key: stripeSettings.live_publishable_key,
        is_live_mode: stripeSettings.is_live_mode
      };
      
      // Only include secret keys if they've been entered (not empty)
      if (stripeSettings.test_secret_key) {
        updateData.test_secret_key = stripeSettings.test_secret_key;
      }
      if (stripeSettings.live_secret_key) {
        updateData.live_secret_key = stripeSettings.live_secret_key;
      }
      
      const res = await api.put("/platform/stripe-settings", updateData);
      setStripeSettings({
        ...res.data,
        test_secret_key: "",
        live_secret_key: ""
      });
      toast.success("Stripe settings updated successfully");
    } catch (error) {
      console.error("Error updating Stripe settings:", error);
      toast.error("Failed to update Stripe settings");
    } finally {
      setSavingStripe(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const updateData = {
        sender_email: emailSettings.sender_email,
        sender_name: emailSettings.sender_name
      };
      
      // Only include API key if it's been entered
      if (emailSettings.api_key) {
        updateData.api_key = emailSettings.api_key;
      }
      
      const res = await api.post("/admin/email-settings", updateData);
      setEmailSettings({
        ...res.data,
        api_key: ""
      });
      toast.success("Email settings updated successfully");
    } catch (error) {
      console.error("Error updating email settings:", error);
      toast.error("Failed to update email settings");
    } finally {
      setSavingEmail(false);
    }
  };

  // Cancel subscription handler
  const handleOpenCancelDialog = (company) => {
    setCancellingCompany(company);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const handleCancelSubscription = async () => {
    if (!cancellingCompany) return;
    
    setCancelling(true);
    try {
      await api.post(`/platform/companies/${cancellingCompany.id}/cancel-subscription`, {
        reason: cancelReason || "Cancelled by admin"
      });
      toast.success(`Subscription cancelled for ${cancellingCompany.name}`);
      setCancelDialogOpen(false);
      // Refresh companies list
      const companiesRes = await api.get("/platform/companies");
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  // Delete company handler
  const handleOpenDeleteDialog = async (company) => {
    setDeletingCompany(company);
    setDeleteConfirmText("");
    setCompanyDetails(null);
    setDeleteDialogOpen(true);
    
    // Fetch company details to show data counts
    try {
      const res = await api.get(`/platform/companies/${company.id}/details`);
      setCompanyDetails(res.data);
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  };

  const handleDeleteCompany = async () => {
    if (!deletingCompany || deleteConfirmText !== deletingCompany.name) return;
    
    setDeleting(true);
    try {
      await api.delete(`/platform/companies/${deletingCompany.id}`, {
        data: {
          confirm_delete: true,
          cancel_subscription: true
        }
      });
      toast.success(`Company "${deletingCompany.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      // Refresh companies list and stats
      const [companiesRes, statsRes] = await Promise.all([
        api.get("/platform/companies"),
        api.get("/platform/stats")
      ]);
      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error(error.response?.data?.detail || "Failed to delete company");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    const styles = {
      trial: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
      expired: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      cancelled: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
    };
    
    const icons = {
      trial: <Clock className="w-3 h-3" />,
      active: <CheckCircle2 className="w-3 h-3" />,
      expired: <AlertTriangle className="w-3 h-3" />,
      cancelled: <AlertTriangle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.trial}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <RotalieLogo showText={false} />
              <div>
                <h1 className="text-xl font-bold tracking-tight font-['Inter']">Rotalie</h1>
                <p className="text-xs text-muted-foreground">Platform Administration</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto p-4 md:p-8 space-y-8">
        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <TrendingUp className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-packages">
              <Package className="w-4 h-4 mr-2" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="database" data-testid="tab-database">
              <Database className="w-4 h-4 mr-2" />
              Database
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-8 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Companies</p>
                  <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-2xl font-bold">{stats?.active_subscriptions || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Trials</p>
                  <p className="text-2xl font-bold">{stats?.active_trials || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">MRR</p>
                  <p className="text-2xl font-bold">£{(stats?.mrr || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Stats */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
              <CardDescription>Monthly and annual recurring revenue</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-muted-foreground">Monthly Recurring Revenue</span>
                  </div>
                  <span className="text-xl font-bold">£{(stats?.mrr || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <span className="text-muted-foreground">Annual Recurring Revenue</span>
                  </div>
                  <span className="text-xl font-bold">£{(stats?.arr || 0).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Subscription Status</CardTitle>
              <CardDescription>Breakdown by status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-muted-foreground">Active Trials</span>
                  <span className="font-medium">{stats?.active_trials || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-muted-foreground">Active Subscriptions</span>
                  <span className="font-medium">{stats?.active_subscriptions || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-muted-foreground">Expired Trials</span>
                  <span className="font-medium">{stats?.expired_trials || 0}</span>
                </div>
                <div className="h-px bg-border my-2" />
                <div className="flex items-center justify-between py-2 font-medium">
                  <span>Total Companies</span>
                  <span>{stats?.total_companies || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Companies</CardTitle>
            <CardDescription>
              Manage company subscriptions and trial periods
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trial Days</TableHead>
                    <TableHead>Monthly Price</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                        No companies registered yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-sm text-muted-foreground">{company.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{company.company_code}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            {company.employee_count || 0}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(company.subscription_status)}</TableCell>
                        <TableCell>
                          {company.trial_days_remaining > 0 ? (
                            <span className="text-amber-600 dark:text-amber-400 font-medium">{company.trial_days_remaining} days left</span>
                          ) : (
                            <span className="text-muted-foreground">Expired</span>
                          )}
                        </TableCell>
                        <TableCell>
                          £{(company.monthly_price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {company.created_at ? format(new Date(company.created_at), "MMM d, yyyy") : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`company-actions-${company.id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {company.has_stripe_subscription && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleOpenCancelDialog(company)}
                                    className="cursor-pointer text-amber-600 focus:text-amber-600"
                                  >
                                    <Ban className="w-4 h-4 mr-2" />
                                    Cancel Subscription
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              
                              <DropdownMenuItem 
                                onClick={() => handleOpenDeleteDialog(company)}
                                className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Company
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Subscription Packages
                </CardTitle>
                <CardDescription>
                  Manage subscription plans and pricing for each currency (GBP, EUR, USD)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Subscription Plans */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Subscription Plans</h3>
                    <div className="grid gap-6">
                      {packages.filter(p => p.type === "subscription").map((pkg) => (
                        <Card key={pkg.id} className="border">
                          <CardContent className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  pkg.tier === "light" ? "bg-slate-100" :
                                  pkg.tier === "standard" ? "bg-blue-100" :
                                  "bg-purple-100"
                                }`}>
                                  <DollarSign className={`w-5 h-5 ${
                                    pkg.tier === "light" ? "text-slate-600" :
                                    pkg.tier === "standard" ? "text-blue-600" :
                                    "text-purple-600"
                                  }`} />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-lg">{pkg.name}</h4>
                                  <p className="text-sm text-muted-foreground">{pkg.tier} tier</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={pkg.enabled !== false}
                                  onCheckedChange={async (enabled) => {
                                    try {
                                      await api.put(`/admin/packages/${pkg.id}`, { enabled });
                                      setPackages(prev => prev.map(p => 
                                        p.id === pkg.id ? { ...p, enabled } : p
                                      ));
                                      toast.success(`${pkg.name} ${enabled ? 'enabled' : 'disabled'}`);
                                    } catch (error) {
                                      toast.error("Failed to update package");
                                    }
                                  }}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {pkg.enabled !== false ? "Enabled" : "Disabled"}
                                </span>
                              </div>
                            </div>

                            {/* Description */}
                            <div className="mb-4">
                              <Label className="text-sm">Description</Label>
                              <Input
                                value={pkg.description || ""}
                                onChange={(e) => {
                                  setPackages(prev => prev.map(p => 
                                    p.id === pkg.id ? { ...p, description: e.target.value } : p
                                  ));
                                }}
                                placeholder="Package description"
                                className="mt-1"
                              />
                            </div>

                            {/* Max Employees */}
                            <div className="mb-4">
                              <Label className="text-sm">Max Employees</Label>
                              <div className="flex items-center gap-3 mt-1">
                                <Input
                                  type="number"
                                  min="-1"
                                  value={pkg.max_employees || 0}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 0;
                                    setPackages(prev => prev.map(p => 
                                      p.id === pkg.id ? { ...p, max_employees: val } : p
                                    ));
                                  }}
                                  className="w-32"
                                />
                                <span className="text-sm text-muted-foreground">
                                  {pkg.max_employees === -1 ? "(Unlimited)" : "employees"}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Set to -1 for unlimited</p>
                            </div>
                            
                            {/* Pricing Grid */}
                            <div className="mb-4">
                              <Label className="text-sm mb-2 block">Monthly Pricing</Label>
                              <div className="grid grid-cols-3 gap-4">
                                {["gbp", "eur", "usd"].map((curr) => (
                                  <div key={curr} className="space-y-1">
                                    <Label className="text-xs uppercase text-muted-foreground">
                                      {currencies[curr]?.symbol || "$"} {curr.toUpperCase()}
                                    </Label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                        {currencies[curr]?.symbol || "$"}
                                      </span>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={pkg.prices?.[curr] || 0}
                                        onChange={(e) => {
                                          const newPrice = parseFloat(e.target.value) || 0;
                                          setPackages(prev => prev.map(p => 
                                            p.id === pkg.id 
                                              ? { ...p, prices: { ...p.prices, [curr]: newPrice } }
                                              : p
                                          ));
                                        }}
                                        className="pl-8"
                                        data-testid={`price-${pkg.id}-${curr}`}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Feature Flags */}
                            <div className="mb-4">
                              <Label className="text-sm mb-2 block">Feature Access Controls</Label>
                              <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-2 border rounded-lg bg-muted/20">
                                {Object.entries(ALL_FEATURE_FLAGS).map(([flagKey, flagMeta]) => (
                                  <div key={flagKey} className="flex items-start gap-2">
                                    <Checkbox
                                      id={`${pkg.id}-${flagKey}`}
                                      checked={pkg.feature_flags?.[flagKey] === true}
                                      onCheckedChange={(checked) => {
                                        setPackages(prev => prev.map(p => 
                                          p.id === pkg.id 
                                            ? { 
                                                ...p, 
                                                feature_flags: { 
                                                  ...p.feature_flags, 
                                                  [flagKey]: checked 
                                                } 
                                              } 
                                            : p
                                        ));
                                      }}
                                      data-testid={`feature-${pkg.id}-${flagKey}`}
                                    />
                                    <div className="flex-1">
                                      <Label 
                                        htmlFor={`${pkg.id}-${flagKey}`}
                                        className="text-sm font-medium cursor-pointer"
                                      >
                                        {flagMeta.name}
                                      </Label>
                                      <p className="text-xs text-muted-foreground">{flagMeta.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Display Features (marketing text) */}
                            <div className="mb-4">
                              <Label className="text-sm mb-2 block">Display Features (shown on pricing page)</Label>
                              <div className="space-y-2">
                                {(pkg.display_features || []).map((feature, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <Input
                                      value={feature}
                                      onChange={(e) => {
                                        const newFeatures = [...(pkg.display_features || [])];
                                        newFeatures[idx] = e.target.value;
                                        setPackages(prev => prev.map(p => 
                                          p.id === pkg.id ? { ...p, display_features: newFeatures } : p
                                        ));
                                      }}
                                      className="flex-1"
                                      placeholder="Marketing feature description"
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const newFeatures = (pkg.display_features || []).filter((_, i) => i !== idx);
                                        setPackages(prev => prev.map(p => 
                                          p.id === pkg.id ? { ...p, display_features: newFeatures } : p
                                        ));
                                      }}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const newFeatures = [...(pkg.display_features || []), "New feature"];
                                    setPackages(prev => prev.map(p => 
                                      p.id === pkg.id ? { ...p, display_features: newFeatures } : p
                                    ));
                                  }}
                                >
                                  + Add Display Feature
                                </Button>
                              </div>
                            </div>
                            
                            {/* Save Button */}
                            <div className="flex justify-end border-t pt-4">
                              <Button
                                onClick={async () => {
                                  setSavingPackage(pkg.id);
                                  try {
                                    await api.put(`/admin/packages/${pkg.id}`, {
                                      prices: pkg.prices,
                                      description: pkg.description,
                                      max_employees: pkg.max_employees,
                                      feature_flags: pkg.feature_flags,
                                      display_features: pkg.display_features
                                    });
                                    toast.success(`${pkg.name} updated successfully`);
                                  } catch (error) {
                                    toast.error("Failed to save package");
                                  } finally {
                                    setSavingPackage(null);
                                  }
                                }}
                                disabled={savingPackage === pkg.id}
                                data-testid={`save-${pkg.id}`}
                              >
                                {savingPackage === pkg.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <Save className="w-4 h-4 mr-2" />
                                )}
                                Save Changes
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Staff Pack Add-ons */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Staff Pack Add-ons</h3>
                    <div className="grid gap-4">
                      {packages.filter(p => p.type === "addon").map((pkg) => (
                        <Card key={pkg.id} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                  <Users className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold">{pkg.name}</h4>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={pkg.employees || 0}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value) || 0;
                                      setPackages(prev => prev.map(p => 
                                        p.id === pkg.id ? { ...p, employees: val } : p
                                      ));
                                    }}
                                    className="w-24 mt-1"
                                  />
                                  <span className="text-sm text-muted-foreground ml-2">employees</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={pkg.enabled !== false}
                                  onCheckedChange={async (enabled) => {
                                    try {
                                      await api.put(`/admin/packages/${pkg.id}`, { enabled });
                                      setPackages(prev => prev.map(p => 
                                        p.id === pkg.id ? { ...p, enabled } : p
                                      ));
                                      toast.success(`${pkg.name} ${enabled ? 'enabled' : 'disabled'}`);
                                    } catch (error) {
                                      toast.error("Failed to update package");
                                    }
                                  }}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {pkg.enabled !== false ? "Enabled" : "Disabled"}
                                </span>
                              </div>
                            </div>
                            
                            {/* Pricing Grid */}
                            <div className="grid grid-cols-3 gap-4">
                              {["gbp", "eur", "usd"].map((curr) => (
                                <div key={curr} className="space-y-2">
                                  <Label className="text-xs uppercase text-muted-foreground">
                                    {currencies[curr]?.symbol || "$"} {curr.toUpperCase()}
                                  </Label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                      {currencies[curr]?.symbol || "$"}
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={pkg.prices?.[curr] || 0}
                                      onChange={(e) => {
                                        const newPrice = parseFloat(e.target.value) || 0;
                                        setPackages(prev => prev.map(p => 
                                          p.id === pkg.id 
                                            ? { ...p, prices: { ...p.prices, [curr]: newPrice } }
                                            : p
                                        ));
                                      }}
                                      className="pl-8"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Save Button */}
                            <div className="mt-4 flex justify-end">
                              <Button
                                size="sm"
                                onClick={async () => {
                                  setSavingPackage(pkg.id);
                                  try {
                                    await api.put(`/admin/packages/${pkg.id}`, {
                                      prices: pkg.prices,
                                      employees: pkg.employees
                                    });
                                    toast.success(`${pkg.name} updated`);
                                  } catch (error) {
                                    toast.error("Failed to save");
                                  } finally {
                                    setSavingPackage(null);
                                  }
                                }}
                                disabled={savingPackage === pkg.id}
                              >
                                {savingPackage === pkg.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Pricing
                                  </>
                                )}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Settings (Resend)
                </CardTitle>
                <CardDescription>
                  Configure transactional email settings. Used for verification emails, welcome emails, and notifications.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* API Key */}
                <div className="space-y-2">
                  <Label htmlFor="resend-api-key">Resend API Key</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        id="resend-api-key"
                        type={showEmailApiKey ? "text" : "password"}
                        placeholder={emailSettings.api_key_set ? "••••••••••••••••••••" : "re_xxxxx..."}
                        value={emailSettings.api_key}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, api_key: e.target.value }))}
                        data-testid="resend-api-key-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowEmailApiKey(!showEmailApiKey)}
                      >
                        {showEmailApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get your API key from{" "}
                    <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      resend.com/api-keys
                    </a>
                  </p>
                  {emailSettings.api_key_set && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> API key is configured
                    </p>
                  )}
                </div>

                {/* Sender Email */}
                <div className="space-y-2">
                  <Label htmlFor="sender-email">Sender Email</Label>
                  <Input
                    id="sender-email"
                    type="email"
                    placeholder="noreply@yourdomain.com"
                    value={emailSettings.sender_email}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, sender_email: e.target.value }))}
                    data-testid="sender-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use <code className="bg-muted px-1 rounded">onboarding@resend.dev</code> for testing, or add your own domain in Resend.
                  </p>
                </div>

                {/* Sender Name */}
                <div className="space-y-2">
                  <Label htmlFor="sender-name">Sender Name</Label>
                  <Input
                    id="sender-name"
                    placeholder="Rotalie"
                    value={emailSettings.sender_name}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, sender_name: e.target.value }))}
                    data-testid="sender-name-input"
                  />
                </div>

                {/* Email Types Info */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Automated Emails</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Email verification on signup (6-digit code)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Welcome email when subscribed
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Confirmation when upgrading plans
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Trial expiry reminders (7, 3, 1 day)
                    </li>
                  </ul>
                </div>

                {/* Test Email Section */}
                {emailSettings.api_key_set && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <TestTube className="w-4 h-4" />
                      Send Test Email
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {["verification", "welcome", "upgrade", "trial_expiry"].map((type) => (
                        <Button
                          key={type}
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const testEmail = prompt("Enter email address to send test to:");
                              if (!testEmail) return;
                              
                              const res = await api.post("/admin/test-email", {
                                to_email: testEmail,
                                email_type: type
                              });
                              toast.success(`Test ${type} email sent!`);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || "Failed to send test email");
                            }
                          }}
                          data-testid={`test-email-${type}`}
                        >
                          {type.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={handleSaveEmailSettings}
                    disabled={savingEmail}
                    data-testid="save-email-settings"
                  >
                    {savingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Email Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database" className="mt-6">
            <DatabaseManager />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            {/* Platform Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Platform Settings
                </CardTitle>
                <CardDescription>Configure default settings for the platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="defaultTrialDays">Default Trial Days</Label>
                    <Input
                      id="defaultTrialDays"
                      type="number"
                      value={settings?.default_trial_days || 30}
                      onChange={(e) => setSettings({ ...settings, default_trial_days: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="defaultPrice">Default Monthly Price (£)</Label>
                    <Input
                      id="defaultPrice"
                      type="number"
                      step="0.01"
                      value={settings?.default_monthly_price || 29}
                      onChange={(e) => setSettings({ ...settings, default_monthly_price: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Platform Settings
                </Button>
              </CardContent>
            </Card>

            {/* Stripe Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Stripe Integration
                    </CardTitle>
                    <CardDescription>Configure Stripe API keys for payment processing</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      stripeSettings.is_live_mode 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    }`}>
                      {stripeSettings.is_live_mode ? (
                        <>
                          <Zap className="w-3.5 h-3.5" />
                          Live Mode
                        </>
                      ) : (
                        <>
                          <TestTube className="w-3.5 h-3.5" />
                          Test Mode
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mode Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="text-base">Live Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      {stripeSettings.is_live_mode 
                        ? "Processing real payments with live Stripe keys" 
                        : "Using test keys for sandbox testing"}
                    </p>
                  </div>
                  <Switch
                    checked={stripeSettings.is_live_mode}
                    onCheckedChange={(checked) => setStripeSettings({ ...stripeSettings, is_live_mode: checked })}
                    data-testid="stripe-live-mode-toggle"
                  />
                </div>

                {/* Test Keys */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <TestTube className="w-4 h-4 text-amber-500" />
                    Test Keys
                  </h4>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="testPublishableKey">Publishable Key</Label>
                      <Input
                        id="testPublishableKey"
                        placeholder="pk_test_..."
                        value={stripeSettings.test_publishable_key}
                        onChange={(e) => setStripeSettings({ ...stripeSettings, test_publishable_key: e.target.value })}
                        data-testid="stripe-test-publishable-key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="testSecretKey">Secret Key</Label>
                      <div className="relative">
                        <Input
                          id="testSecretKey"
                          type={showTestSecretKey ? "text" : "password"}
                          placeholder={stripeSettings.test_secret_key_set ? "••••••••••••••••••••" : "sk_test_..."}
                          value={stripeSettings.test_secret_key}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, test_secret_key: e.target.value })}
                          data-testid="stripe-test-secret-key"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowTestSecretKey(!showTestSecretKey)}
                        >
                          {showTestSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                      {stripeSettings.test_secret_key_set && (
                        <p className="text-xs text-muted-foreground">Secret key is set. Enter a new key to update.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Live Keys */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-500" />
                    Live Keys
                  </h4>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="livePublishableKey">Publishable Key</Label>
                      <Input
                        id="livePublishableKey"
                        placeholder="pk_live_..."
                        value={stripeSettings.live_publishable_key}
                        onChange={(e) => setStripeSettings({ ...stripeSettings, live_publishable_key: e.target.value })}
                        data-testid="stripe-live-publishable-key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="liveSecretKey">Secret Key</Label>
                      <div className="relative">
                        <Input
                          id="liveSecretKey"
                          type={showLiveSecretKey ? "text" : "password"}
                          placeholder={stripeSettings.live_secret_key_set ? "••••••••••••••••••••" : "sk_live_..."}
                          value={stripeSettings.live_secret_key}
                          onChange={(e) => setStripeSettings({ ...stripeSettings, live_secret_key: e.target.value })}
                          data-testid="stripe-live-secret-key"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowLiveSecretKey(!showLiveSecretKey)}
                        >
                          {showLiveSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                      {stripeSettings.live_secret_key_set && (
                        <p className="text-xs text-muted-foreground">Secret key is set. Enter a new key to update.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-4 border-t">
                  <Button onClick={handleSaveStripeSettings} disabled={savingStripe} data-testid="save-stripe-settings-btn">
                    {savingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Stripe Settings
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Get your API keys from{" "}
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Stripe Dashboard
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Ban className="w-5 h-5" />
              Cancel Subscription
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the subscription for <strong>{cancellingCompany?.name}</strong>?
              This will immediately cancel their Stripe subscription and revoke access to paid features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Current plan:</strong> {cancellingCompany?.subscription_tier || "Unknown"}
                <br />
                <strong>Monthly price:</strong> £{(cancellingCompany?.monthly_price || 0).toFixed(2)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Cancellation Reason (optional)</Label>
              <Input
                id="cancel-reason"
                placeholder="e.g., Customer requested, Non-payment, etc."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Cancel Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Company Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Company
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete <strong>{deletingCompany?.name}</strong> and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Warning if has active subscription */}
            {companyDetails?.has_stripe_subscription && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Active Subscription Detected</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      This company has an active Stripe subscription. It will be automatically cancelled when you delete the company.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Data counts */}
            {companyDetails?.data_counts && (
              <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                <p className="text-sm font-medium">Data that will be deleted:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Employees:</span>
                    <span className="font-medium">{companyDetails.data_counts.employees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shifts:</span>
                    <span className="font-medium">{companyDetails.data_counts.shifts}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Absences:</span>
                    <span className="font-medium">{companyDetails.data_counts.absences}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Break Types:</span>
                    <span className="font-medium">{companyDetails.data_counts.break_types}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Confirm by typing company name */}
            <div className="space-y-2">
              <Label htmlFor="delete-confirm">
                Type <strong>{deletingCompany?.name}</strong> to confirm
              </Label>
              <Input
                id="delete-confirm"
                placeholder="Enter company name"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              disabled={deleting || deleteConfirmText !== deletingCompany?.name}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Company
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
